"use client";
import * as React from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Plus, ArrowRight, BookOpen, Truck, Package, FileText, Wallet, CheckCircle2, Eye, Send, RefreshCw, Upload, ScanLine, Loader2, X } from "lucide-react";
import { SimpleTabs, type SimpleTab } from "../SimpleTabs";
import { DataTable, type Column } from "../DataTable";
import { useERP, formatBDT, formatNum, type CustomerReceipt, type ID } from "@/lib/erp/store";
import { useToast } from "@/hooks/use-toast";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";

export interface SalesModuleProps {
  onNavigate: (pageId: string) => void;
}

export function SalesModule({ onNavigate }: SalesModuleProps) {
  const erp = useERP();
  const [tab, setTab] = React.useState("so");
  const [uploadOpen, setUploadOpen] = React.useState(false);
  const tabs: SimpleTab[] = [
    { value: "so", label: "Sales Orders", count: erp.salesOrders.length },
    { value: "inv", label: "Invoices", count: erp.salesInvoices.length },
    { value: "rct", label: "Receipts", count: erp.customerReceipts.length },
    { value: "guide", label: "Order-to-Cash Guide" },
  ];
  return (
    <div className="flex flex-col gap-6">
      <div className="flex flex-col gap-2 sm:flex-row sm:items-start sm:justify-between">
        <div>
          <h1 className="text-xl font-semibold sm:text-2xl">Sales</h1>
          <p className="text-muted-foreground mt-1 text-sm">Manage sales orders, invoicing and cash collection.</p>
        </div>
        <div className="flex gap-2">
          <Button size="sm" variant="outline" onClick={() => setUploadOpen(true)}><Upload className="size-4" /> Upload Order</Button>
          <Button size="sm" onClick={() => onNavigate("so-new")}><Plus className="size-4" /> New SO</Button>
        </div>
      </div>
      <SimpleTabs value={tab} onValueChange={setTab} tabs={tabs} />
      {tab === "so" && <SOTab onNavigate={onNavigate} />}
      {tab === "inv" && <InvoiceTab onNavigate={onNavigate} />}
      {tab === "rct" && <ReceiptTab onNavigate={onNavigate} />}
      {tab === "guide" && <O2CGuide />}
      <UploadOrderDialog open={uploadOpen} onOpenChange={setUploadOpen} onCreated={() => setTab("so")} />
    </div>
  );
}

function StatCard({ label, value, tone }: { label: string; value: string; tone?: string }) {
  return (
    <Card>
      <CardHeader><CardTitle className="text-muted-foreground text-xs font-medium uppercase tracking-wide">{label}</CardTitle></CardHeader>
      <CardContent><div className={`text-2xl font-semibold ${tone || ""}`}>{value}</div></CardContent>
    </Card>
  );
}

// ===== Dispatch dialog — pick vehicle & driver =====
function DispatchDialog({ open, onOpenChange, soId, onConfirm }: {
  open: boolean; onOpenChange: (v: boolean) => void; soId: string | null;
  onConfirm: (vehicleId: string, driverId: string) => void;
}) {
  const erp = useERP();
  const availableVehicles = erp.vehicles.filter(v => v.status === "Available");
  const drivers = erp.employees.filter(e => e.department === "Logistics" || e.designation.includes("Driver"));
  const [vehicleId, setVehicleId] = React.useState("");
  const [driverId, setDriverId] = React.useState("");
  React.useEffect(() => { if (open) { setVehicleId(availableVehicles[0]?.id ?? ""); setDriverId(drivers[0]?.id ?? ""); } }, [open]);
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader><DialogTitle>Dispatch Sales Order</DialogTitle>
        <DialogDescription>Select vehicle and driver to dispatch this shipment.</DialogDescription></DialogHeader>
        <div className="space-y-3">
          <div>
            <Label>Vehicle</Label>
            <Select value={vehicleId} onValueChange={setVehicleId}>
              <SelectTrigger className="w-full"><SelectValue placeholder="Select vehicle..." /></SelectTrigger>
              <SelectContent>
                {availableVehicles.length === 0 && <SelectItem value="" disabled>No vehicles available</SelectItem>}
                {availableVehicles.map(v => <SelectItem key={v.id} value={v.id}>{v.regNumber} · {v.capacity}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label>Driver</Label>
            <Select value={driverId} onValueChange={setDriverId}>
              <SelectTrigger className="w-full"><SelectValue placeholder="Select driver..." /></SelectTrigger>
              <SelectContent>{drivers.map(d => <SelectItem key={d.id} value={d.id}>{d.name} · {d.designation}</SelectItem>)}</SelectContent>
            </Select>
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button onClick={() => { if (vehicleId && driverId) { onConfirm(vehicleId, driverId); onOpenChange(false); } }} disabled={!vehicleId || !driverId}><Truck className="size-4 mr-1" /> Dispatch</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ===== Deliver dialog — capture COD amount =====
function DeliverDialog({ open, onOpenChange, onConfirm }: {
  open: boolean; onOpenChange: (v: boolean) => void; onConfirm: (codAmount: number) => void;
}) {
  const [cod, setCod] = React.useState("0");
  React.useEffect(() => { if (open) setCod("0"); }, [open]);
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader><DialogTitle>Confirm Delivery</DialogTitle>
        <DialogDescription>Capture COD amount collected at delivery (0 for credit orders).</DialogDescription></DialogHeader>
        <div>
          <Label htmlFor="cod">COD Amount (৳)</Label>
          <Input id="cod" type="number" value={cod} onChange={e => setCod(e.target.value)} autoFocus />
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button onClick={() => { onConfirm(Number(cod) || 0); onOpenChange(false); }}><CheckCircle2 className="size-4 mr-1" /> Confirm Delivery</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ===== New Receipt Dialog =====
function NewReceiptDialog({ open, onOpenChange }: { open: boolean; onOpenChange: (v: boolean) => void }) {
  const erp = useERP();
  const { toast } = useToast();
  const [customerId, setCustomerId] = React.useState("");
  const [amount, setAmount] = React.useState("");
  const [paymentMode, setPaymentMode] = React.useState<CustomerReceipt["paymentMode"]>("Cash");
  const [bankRef, setBankRef] = React.useState("");
  const [invoiceId, setInvoiceId] = React.useState("");

  React.useEffect(() => { if (open) { setCustomerId(erp.customers[0]?.id ?? ""); setAmount(""); setBankRef(""); setInvoiceId(""); } }, [open]);
  // M9 fix: reset invoice selection when customer changes
  React.useEffect(() => { setInvoiceId(""); }, [customerId]);

  const unpaidInvoices = erp.salesInvoices.filter(i => i.customerId === customerId && (i.status === "Posted" || i.status === "Partial"));
  const amountNum = Number(amount) || 0;

  const handleSubmit = () => {
    if (!customerId || amountNum <= 0) { toast({ title: "Validation Error", description: "Select customer and enter valid amount.", variant: "destructive" }); return; }
    const allocations = invoiceId ? [{ invoiceId, amount: amountNum }] : [];
    erp.createReceipt({
      customerId, receiptDate: new Date().toISOString().slice(0, 10),
      amount: amountNum, paymentMode, bankRef: bankRef || undefined, allocations,
    });
    toast({ title: "Receipt Created", description: `Receipt of ${formatBDT(amountNum)} created for ${erp.getCustomer(customerId)?.name}.` });
    onOpenChange(false);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader><DialogTitle>New Customer Receipt</DialogTitle>
        <DialogDescription>Record a customer payment received.</DialogDescription></DialogHeader>
        <div className="space-y-3">
          <div>
            <Label>Customer</Label>
            <Select value={customerId} onValueChange={setCustomerId}>
              <SelectTrigger className="w-full"><SelectValue /></SelectTrigger>
              <SelectContent>{erp.customers.map(c => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div>
            <Label htmlFor="amt">Amount (৳)</Label>
            <Input id="amt" type="number" value={amount} onChange={e => setAmount(e.target.value)} placeholder="0" autoFocus />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label>Payment Mode</Label>
              <Select value={paymentMode} onValueChange={(v) => setPaymentMode(v as CustomerReceipt["paymentMode"])}>
                <SelectTrigger className="w-full"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="Cash">Cash</SelectItem>
                  <SelectItem value="Cheque">Cheque</SelectItem>
                  <SelectItem value="Bank Transfer">Bank Transfer</SelectItem>
                  <SelectItem value="bKash">bKash</SelectItem>
                  <SelectItem value="Nagad">Nagad</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label htmlFor="bankref">Bank Ref (optional)</Label>
              <Input id="bankref" value={bankRef} onChange={e => setBankRef(e.target.value)} placeholder="TXN/CHQ#" />
            </div>
          </div>
          {unpaidInvoices.length > 0 && (
            <div>
              <Label>Allocate to Invoice (optional)</Label>
              <Select value={invoiceId} onValueChange={setInvoiceId}>
                <SelectTrigger className="w-full"><SelectValue placeholder="— Unallocated —" /></SelectTrigger>
                <SelectContent>
                  {unpaidInvoices.map(i => <SelectItem key={i.id} value={i.id}>{i.invoiceNumber} · {formatBDT(i.total - i.paidAmount)} due</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
          )}
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button onClick={handleSubmit}><Wallet className="size-4 mr-1" /> Create Receipt</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function SOTab({ onNavigate }: { onNavigate: (pageId: string) => void }) {
  const erp = useERP();
  const { toast } = useToast();
  const rows = erp.salesOrders;
  const [dispatchOpen, setDispatchOpen] = React.useState(false);
  const [deliverOpen, setDeliverOpen] = React.useState(false);
  const [activeSO, setActiveSO] = React.useState<string | null>(null);

  return (
    <div className="flex flex-col gap-4">
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
        <StatCard label="Total SOs" value={formatNum(rows.length)} />
        <StatCard label="Pending Approval" value={formatNum(rows.filter(r => r.status === "Pending Approval").length)} tone="text-destructive" />
        <StatCard label="Delivered" value={formatNum(rows.filter(r => r.status === "Delivered").length)} tone="text-primary" />
        <StatCard label="Total Value" value={formatBDT(rows.reduce((s, r) => s + r.total, 0))} />
      </div>
      <Card>
        <CardHeader><CardTitle>Sales Orders</CardTitle></CardHeader>
        <CardContent>
          <DataTable
            columns={[
              { key: "soNumber", header: "SO #", sortable: true, render: r => <span className="font-mono text-xs text-primary">{r.soNumber}</span> },
              { key: "customer", header: "Customer", sortable: true, render: r => erp.getCustomer(r.customerId)?.name ?? "—" },
              { key: "orderDate", header: "Order Date", sortable: true },
              { key: "requestedDelivery", header: "Req. Delivery" },
              { key: "total", header: "Total", align: "right", sortable: true, render: r => formatBDT(r.total) },
              { key: "margin", header: "Margin %", align: "right", render: r => <span className={r.margin < 15 ? "text-destructive" : "text-primary"}>{r.margin.toFixed(1)}%</span> },
              { key: "creditCheck", header: "Credit", render: r => <Badge variant={r.creditCheck === "Pass" ? "default" : "destructive"}>{r.creditCheck}</Badge> },
              { key: "status", header: "Status", render: r => <Badge variant={r.status === "Delivered" || r.status === "Invoiced" ? "default" : r.status === "Cancelled" ? "destructive" : "secondary"}>{r.status}</Badge> },
              { key: "actions", header: "Actions", align: "right", render: r => (
                <div className="flex justify-end gap-1" onClick={e => e.stopPropagation()}>
                  <Button size="sm" variant="ghost" className="size-7 p-0" onClick={() => onNavigate(`so-detail:${r.id}`)}><Eye className="size-3.5" /></Button>
                  {r.status === "Pending Approval" && (
                    <Button size="sm" variant="ghost" className="size-7 p-0 text-emerald-600" title="Approve" onClick={() => { erp.approveSO(r.id, "Manager"); toast({ title: "SO Approved", description: `${r.soNumber} has been approved.` }); }}><CheckCircle2 className="size-3.5" /></Button>
                  )}
                  {r.status === "Approved" && (
                    <Button size="sm" variant="outline" className="h-7 text-xs" onClick={() => {
                      // H2 fix: catch pickSO error
                      try { erp.pickSO(r.id); toast({ title: "Picking Started", description: `${r.soNumber} is now in Picking. Stock issued.` }); }
                      catch (e) { toast({ title: "Pick Failed", description: String(e).replace(/^Error:\s*/, ""), variant: "destructive" }); }
                    }}><Package className="size-3 mr-1" />Pick</Button>
                  )}
                  {r.status === "Picking" && (
                    <Button size="sm" variant="outline" className="h-7 text-xs" onClick={() => { setActiveSO(r.id); setDispatchOpen(true); }}><Truck className="size-3 mr-1" />Dispatch</Button>
                  )}
                  {r.status === "Dispatched" && (
                    <Button size="sm" variant="outline" className="h-7 text-xs text-emerald-600" onClick={() => { setActiveSO(r.id); setDeliverOpen(true); }}><CheckCircle2 className="size-3 mr-1" />Deliver</Button>
                  )}
                  {r.status === "Delivered" && (
                    <Button size="sm" variant="outline" className="h-7 text-xs" onClick={() => {
                      // M4 fix: createInvoice validates SO status — wrap in try/catch
                      try { erp.createInvoice(r.id); toast({ title: "Invoice Created", description: `Invoice created from ${r.soNumber}.` }); }
                      catch (e) { toast({ title: "Invoice Failed", description: String(e).replace(/^Error:\s*/, ""), variant: "destructive" }); }
                    }}><FileText className="size-3 mr-1" />Invoice</Button>
                  )}
                </div>
              )},
            ] as Column<typeof rows[number]>[]}
            data={rows}
            rowKey={r => r.id}
            searchKeys={["soNumber"]}
            onRowClick={r => onNavigate(`so-detail:${r.id}`)}
          />
        </CardContent>
      </Card>

      <DispatchDialog
        open={dispatchOpen}
        onOpenChange={setDispatchOpen}
        soId={activeSO}
        onConfirm={(vid, did) => {
          if (activeSO) {
            // H5 fix: wrap dispatchSO in try/catch (vehicle availability check)
            try {
              erp.dispatchSO(activeSO, vid, did);
              toast({ title: "SO Dispatched", description: `Shipment created. Vehicle assigned. SO ${erp.salesOrders.find(s => s.id === activeSO)?.soNumber}.` });
            } catch (e) {
              toast({ title: "Dispatch Failed", description: String(e).replace(/^Error:\s*/, ""), variant: "destructive" });
            }
          }
        }}
      />
      <DeliverDialog
        open={deliverOpen}
        onOpenChange={setDeliverOpen}
        onConfirm={(cod) => {
          if (activeSO) {
            erp.deliverSO(activeSO, cod);
            toast({ title: "Delivery Confirmed", description: `POD captured. COD: ${formatBDT(cod)}.` });
          }
        }}
      />
    </div>
  );
}

function InvoiceTab({ onNavigate }: { onNavigate: (pageId: string) => void }) {
  const erp = useERP();
  const { toast } = useToast();
  const rows = erp.salesInvoices;
  return (
    <div className="flex flex-col gap-4">
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
        <StatCard label="Total Invoices" value={formatNum(rows.length)} />
        <StatCard label="Posted" value={formatNum(rows.filter(r => r.status === "Posted").length)} />
        <StatCard label="Paid" value={formatNum(rows.filter(r => r.status === "Paid").length)} tone="text-primary" />
        <StatCard label="Outstanding" value={formatBDT(rows.reduce((s, r) => s + (r.total - r.paidAmount), 0))} tone="text-destructive" />
      </div>
      <Card>
        <CardHeader className="flex flex-row items-center justify-between">
          <CardTitle>Sales Invoices</CardTitle>
        </CardHeader>
        <CardContent>
          <DataTable
            columns={[
              { key: "invoiceNumber", header: "Invoice #", sortable: true, render: r => <span className="font-mono text-xs text-primary">{r.invoiceNumber}</span> },
              { key: "customer", header: "Customer", sortable: true, render: r => erp.getCustomer(r.customerId)?.name ?? "—" },
              { key: "invoiceDate", header: "Date", sortable: true },
              { key: "amount", header: "Amount", align: "right", sortable: true, render: r => formatBDT(r.amount) },
              { key: "tax", header: "VAT", align: "right", render: r => formatBDT(r.tax) },
              { key: "total", header: "Total", align: "right", render: r => formatBDT(r.total) },
              { key: "paid", header: "Paid", align: "right", render: r => formatBDT(r.paidAmount) },
              { key: "status", header: "Status", render: r => <Badge variant={r.status === "Paid" ? "default" : r.status === "Cancelled" ? "destructive" : "secondary"}>{r.status}</Badge> },
              { key: "actions", header: "Actions", align: "right", render: r => (
                <div className="flex justify-end gap-1" onClick={e => e.stopPropagation()}>
                  <Button size="sm" variant="ghost" className="size-7 p-0" onClick={() => onNavigate(`invoice-detail:${r.id}`)}><Eye className="size-3.5" /></Button>
                  {r.status === "Draft" && (
                    <Button size="sm" variant="outline" className="h-7 text-xs text-emerald-600" onClick={() => { erp.postInvoice(r.id, "Rumana Begum"); toast({ title: "Invoice Posted", description: `${r.invoiceNumber} posted. AR updated. Customer outstanding increased.` }); }}><Send className="size-3 mr-1" />Post</Button>
                  )}
                </div>
              )},
            ] as Column<typeof rows[number]>[]}
            data={rows}
            rowKey={r => r.id}
            searchKeys={["invoiceNumber"]}
            onRowClick={r => onNavigate(`invoice-detail:${r.id}`)}
          />
        </CardContent>
      </Card>
    </div>
  );
}

function ReceiptTab({ onNavigate }: { onNavigate: (pageId: string) => void }) {
  const erp = useERP();
  const { toast } = useToast();
  const rows = erp.customerReceipts;
  const [newOpen, setNewOpen] = React.useState(false);
  return (
    <div className="flex flex-col gap-4">
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
        <StatCard label="Total Receipts" value={formatNum(rows.length)} />
        <StatCard label="Reconciled" value={formatNum(rows.filter(r => r.status === "Reconciled").length)} tone="text-primary" />
        <StatCard label="Pending" value={formatNum(rows.filter(r => r.status === "Pending").length)} tone="text-destructive" />
        <StatCard label="Total Collected" value={formatBDT(rows.reduce((s, r) => s + r.amount, 0))} />
      </div>
      <Card>
        <CardHeader className="flex flex-row items-center justify-between">
          <CardTitle>Customer Receipts</CardTitle>
          <Button size="sm" onClick={() => setNewOpen(true)}><Plus className="size-4" /> New Receipt</Button>
        </CardHeader>
        <CardContent>
          <DataTable
            columns={[
              { key: "receiptNumber", header: "Receipt #", sortable: true, render: r => <span className="font-mono text-xs text-primary">{r.receiptNumber}</span> },
              { key: "customer", header: "Customer", sortable: true, render: r => erp.getCustomer(r.customerId)?.name ?? "—" },
              { key: "receiptDate", header: "Date", sortable: true },
              { key: "amount", header: "Amount", align: "right", sortable: true, render: r => formatBDT(r.amount) },
              { key: "paymentMode", header: "Mode" },
              { key: "bankRef", header: "Bank Ref", render: r => r.bankRef ?? "—" },
              { key: "status", header: "Status", render: r => <Badge variant={r.status === "Reconciled" ? "default" : r.status === "Bounced" ? "destructive" : "secondary"}>{r.status}</Badge> },
              { key: "actions", header: "Actions", align: "right", render: r => (
                <div className="flex justify-end gap-1" onClick={e => e.stopPropagation()}>
                  <Button size="sm" variant="ghost" className="size-7 p-0" onClick={() => onNavigate(`receipt-detail:${r.id}`)}><Eye className="size-3.5" /></Button>
                  {r.status === "Pending" && (
                    <Button size="sm" variant="outline" className="h-7 text-xs text-emerald-600" onClick={() => { erp.reconcileReceipt(r.id); toast({ title: "Receipt Reconciled", description: `${r.receiptNumber} reconciled. AR reduced. JE posted.` }); }}><RefreshCw className="size-3 mr-1" />Reconcile</Button>
                  )}
                </div>
              )},
            ] as Column<typeof rows[number]>[]}
            data={rows}
            rowKey={r => r.id}
            searchKeys={["receiptNumber"]}
            onRowClick={r => onNavigate(`receipt-detail:${r.id}`)}
          />
        </CardContent>
      </Card>
      <NewReceiptDialog open={newOpen} onOpenChange={setNewOpen} />
    </div>
  );
}

function O2CGuide() {
  const steps = [
    { n: 1, title: "Create Sales Order", desc: "Sales rep creates SO against customer; credit check runs automatically.", icon: <FileText className="size-4" /> },
    { n: 2, title: "Approve", desc: "Manager approves SO if credit check failed or margin < 10%.", icon: <CheckCircle2 className="size-4" /> },
    { n: 3, title: "Pick", desc: "Warehouse picks goods; system verifies stock and posts stock ledger.", icon: <Package className="size-4" /> },
    { n: 4, title: "Dispatch", desc: "Logistics dispatches shipment with vehicle and driver assignment.", icon: <Truck className="size-4" /> },
    { n: 5, title: "Deliver", desc: "Driver delivers and captures POD signature.", icon: <CheckCircle2 className="size-4" /> },
    { n: 6, title: "Invoice", desc: "Finance raises and posts sales invoice (DR AR, CR Sales + VAT).", icon: <FileText className="size-4" /> },
    { n: 7, title: "Receipt & Reconcile", desc: "Customer payment received, allocated, and reconciled to invoice.", icon: <Wallet className="size-4" /> },
  ];
  return (
    <Card>
      <CardHeader><CardTitle className="flex items-center gap-2"><BookOpen className="size-5" /> Order-to-Cash Workflow</CardTitle></CardHeader>
      <CardContent>
        <ol className="flex flex-col gap-4">
          {steps.map(s => (
            <li key={s.n} className="flex gap-4">
              <div className="bg-primary text-primary-foreground flex size-9 shrink-0 items-center justify-center rounded-full text-sm font-semibold">{s.n}</div>
              <div className="flex-1">
                <p className="flex items-center gap-2 font-semibold">{s.icon}{s.title}</p>
                <p className="text-muted-foreground text-sm">{s.desc}</p>
              </div>
              {s.n < steps.length && <div className="text-muted-foreground mt-9 hidden sm:block"><ArrowRight className="size-4" /></div>}
            </li>
          ))}
        </ol>
      </CardContent>
    </Card>
  );
}

// ===== Upload Order Dialog (OCR from hardcopy image) =====
interface OcrLine { productName: string; qty: number; unitPrice: number; }
interface OcrResult {
  customerName: string; customerCode: string; orderDate: string;
  lines: OcrLine[]; notes: string; totalAmount: number; confidence: string;
}

function UploadOrderDialog({ open, onOpenChange, onCreated }: {
  open: boolean; onOpenChange: (v: boolean) => void; onCreated: () => void;
}) {
  const erp = useERP();
  const { toast } = useToast();
  const [imagePreview, setImagePreview] = React.useState<string | null>(null);
  const [imageBase64, setImageBase64] = React.useState<string | null>(null);
  const [mimeType, setMimeType] = React.useState("image/jpeg");
  const [loading, setLoading] = React.useState(false);
  const [ocrResult, setOcrResult] = React.useState<OcrResult | null>(null);
  const [customerId, setCustomerId] = React.useState("");
  const [warehouseId, setWarehouseId] = React.useState("");
  const [editableLines, setEditableLines] = React.useState<{ productId: string; qty: number; unitPrice: number; productName: string }[]>([]);

  React.useEffect(() => {
    if (open) {
      setImagePreview(null); setImageBase64(null); setOcrResult(null);
      setCustomerId(erp.customers[0]?.id ?? "");
      setWarehouseId(erp.warehouses[0]?.id ?? "");
      setEditableLines([]);
    }
  }, [open]);

  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!file.type.startsWith("image/")) {
      toast({ title: "Invalid file", description: "Please select an image file.", variant: "destructive" });
      return;
    }
    setMimeType(file.type);
    const reader = new FileReader();
    reader.onload = () => {
      const result = reader.result as string;
      setImagePreview(result);
      setImageBase64(result.split(",")[1] ?? "");
    };
    reader.readAsDataURL(file);
  };

  const handleScan = async () => {
    if (!imageBase64) { toast({ title: "No image", description: "Please select an image first.", variant: "destructive" }); return; }
    setLoading(true);
    setOcrResult(null);
    try {
      const res = await fetch("/api/ocr-order", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ image: imageBase64, mimeType }),
      });
      const json = await res.json();
      if (!res.ok || !json.success) {
        throw new Error(json.error || json.details || "OCR failed");
      }
      const data = json.data as OcrResult;
      setOcrResult(data);
      // Try to match customer by name or code
      const matched = data.customerName
        ? erp.customers.find(c => c.name.toLowerCase().includes(data.customerName.toLowerCase()) || (data.customerCode && c.code === data.customerCode))
        : undefined;
      if (matched) setCustomerId(matched.id);
      // Map OCR lines to products (fuzzy match by name)
      const mapped = (data.lines || []).map(l => {
        const product = erp.products.find(p => p.name.toLowerCase().includes(l.productName.toLowerCase()) || p.sku.toLowerCase() === l.productName.toLowerCase());
        return {
          productId: product?.id ?? "",
          qty: l.qty || 1,
          unitPrice: l.unitPrice || product?.salePrice || 0,
          productName: l.productName,
        };
      });
      setEditableLines(mapped);
      toast({
        title: "OCR Complete",
        description: `Extracted ${mapped.length} line(s). Confidence: ${data.confidence}. Review and edit before creating SO.`,
      });
    } catch (e) {
      toast({ title: "OCR Failed", description: String(e).replace(/^Error:\s*/, ""), variant: "destructive" });
    } finally {
      setLoading(false);
    }
  };

  const handleCreateSO = () => {
    if (!customerId || !warehouseId) { toast({ title: "Customer and warehouse required", variant: "destructive" }); return; }
    const validLines = editableLines.filter(l => l.productId && l.qty > 0);
    if (validLines.length === 0) { toast({ title: "No valid lines", description: "Add at least one line with a product and quantity.", variant: "destructive" }); return; }
    try {
      const id = erp.createSO({
        customerId, warehouseId,
        orderDate: ocrResult?.orderDate || new Date().toISOString().slice(0, 10),
        requestedDelivery: new Date(Date.now() + 3 * 86400000).toISOString().slice(0, 10),
        lines: validLines.map(l => ({ productId: l.productId, qty: l.qty, unitPrice: l.unitPrice, discount: 0, taxRate: 15 })),
        notes: ocrResult?.notes || `Uploaded from image — confidence: ${ocrResult?.confidence ?? "unknown"}`,
      });
      const so = erp.salesOrders.find(s => s.id === id);
      toast({ title: "SO Created from Image", description: `${so?.soNumber ?? "New SO"} created with ${validLines.length} line(s) from uploaded order.` });
      onCreated();
      onOpenChange(false);
    } catch (e) {
      toast({ title: "Failed to create SO", description: String(e).replace(/^Error:\s*/, ""), variant: "destructive" });
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-3xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2"><ScanLine className="size-5" /> Upload Sales Order (OCR)</DialogTitle>
          <DialogDescription>Upload a photo or scan of a handwritten/printed sales order. AI will extract customer and line items for review.</DialogDescription>
        </DialogHeader>
        <div className="space-y-4">
          {/* Image upload + preview */}
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
            <div>
              <Label htmlFor="file-upload">Select Order Image</Label>
              <Input id="file-upload" type="file" accept="image/*" onChange={handleFileSelect} className="cursor-pointer" />
              {imagePreview && (
                <div className="mt-2 relative rounded-md border overflow-hidden">
                  <img src={imagePreview} alt="Order preview" className="w-full h-48 object-contain bg-muted" />
                  <Button size="icon" variant="secondary" className="absolute top-1 right-1 size-6" onClick={() => { setImagePreview(null); setImageBase64(null); setOcrResult(null); }}><X className="size-3" /></Button>
                </div>
              )}
              <Button className="mt-2 w-full" onClick={handleScan} disabled={!imageBase64 || loading}>
                {loading ? <><Loader2 className="size-4 mr-1 animate-spin" /> Scanning...</> : <><ScanLine className="size-4 mr-1" /> Scan with AI</>}
              </Button>
            </div>
            <div className="space-y-2">
              {ocrResult && (
                <>
                  <div className="rounded-md border bg-muted/30 p-3 text-sm space-y-1">
                    <div className="flex justify-between"><span className="text-muted-foreground">Customer (OCR):</span><span className="font-medium">{ocrResult.customerName || "—"}</span></div>
                    <div className="flex justify-between"><span className="text-muted-foreground">Order Date:</span><span>{ocrResult.orderDate || "—"}</span></div>
                    <div className="flex justify-between"><span className="text-muted-foreground">Total (OCR):</span><span className="font-medium">{formatBDT(ocrResult.totalAmount || 0)}</span></div>
                    <div className="flex justify-between"><span className="text-muted-foreground">Confidence:</span><Badge variant={ocrResult.confidence === "high" ? "default" : ocrResult.confidence === "medium" ? "secondary" : "destructive"}>{ocrResult.confidence}</Badge></div>
                  </div>
                  <div>
                    <Label>Match to Customer</Label>
                    <Select value={customerId} onValueChange={setCustomerId}>
                      <SelectTrigger className="w-full"><SelectValue /></SelectTrigger>
                      <SelectContent>{erp.customers.map(c => <SelectItem key={c.id} value={c.id}>{c.name} ({c.code})</SelectItem>)}</SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label>Ship From Warehouse</Label>
                    <Select value={warehouseId} onValueChange={setWarehouseId}>
                      <SelectTrigger className="w-full"><SelectValue /></SelectTrigger>
                      <SelectContent>{erp.warehouses.map(w => <SelectItem key={w.id} value={w.id}>{w.name}</SelectItem>)}</SelectContent>
                    </Select>
                  </div>
                </>
              )}
            </div>
          </div>

          {/* Editable lines */}
          {ocrResult && editableLines.length > 0 && (
            <div className="rounded-md border">
              <div className="grid grid-cols-12 gap-2 bg-muted/50 px-3 py-2 text-xs font-semibold uppercase">
                <div className="col-span-5">Product (OCR → Match)</div>
                <div className="col-span-2 text-right">Qty</div>
                <div className="col-span-3 text-right">Unit Price (৳)</div>
                <div className="col-span-2 text-right">Subtotal</div>
              </div>
              {editableLines.map((l, i) => (
                <div key={i} className="grid grid-cols-12 gap-2 border-t px-3 py-2 items-center">
                  <div className="col-span-5">
                    <Select value={l.productId} onValueChange={(v) => setEditableLines(prev => prev.map((x, idx) => idx === i ? { ...x, productId: v, productName: erp.getProduct(v)?.name ?? x.productName } : x))}>
                      <SelectTrigger className="h-8 text-xs"><SelectValue placeholder={l.productName || "Select..."} /></SelectTrigger>
                      <SelectContent>{erp.products.map(p => <SelectItem key={p.id} value={p.id}>{p.sku} — {p.name}</SelectItem>)}</SelectContent>
                    </Select>
                  </div>
                  <Input className="col-span-2 h-8 text-right text-xs" type="number" value={l.qty} onChange={e => setEditableLines(prev => prev.map((x, idx) => idx === i ? { ...x, qty: Number(e.target.value) || 0 } : x))} />
                  <Input className="col-span-3 h-8 text-right text-xs" type="number" value={l.unitPrice} onChange={e => setEditableLines(prev => prev.map((x, idx) => idx === i ? { ...x, unitPrice: Number(e.target.value) || 0 } : x))} />
                  <div className="col-span-2 text-right text-xs font-medium">{formatBDT(l.qty * l.unitPrice)}</div>
                </div>
              ))}
              <div className="flex justify-between border-t px-3 py-2 text-sm font-semibold">
                <span>Total:</span>
                <span>{formatBDT(editableLines.reduce((s, l) => s + l.qty * l.unitPrice, 0))}</span>
              </div>
            </div>
          )}

          {/* Notes */}
          {ocrResult?.notes && (
            <div>
              <Label>Notes (from OCR)</Label>
              <Textarea value={ocrResult.notes} readOnly rows={2} className="text-xs" />
            </div>
          )}
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button onClick={handleCreateSO} disabled={!ocrResult || editableLines.filter(l => l.productId && l.qty > 0).length === 0}>
            <Plus className="size-4 mr-1" /> Create Sales Order
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
